/**
 * Admin V3 — Overview — Commercial backlog card (F2)
 *
 * Surfaces the operator commercial backlog (ACCEPTED Devis / DRAFT BdC /
 * À planifier) on the dashboard so pending work is discovered on entry rather
 * than by manual searching. Counts come from the single source of truth
 * (useCommercialBacklog → /admin/commercial/work-queue/summary). Each stat is
 * clickable, deep-linking to the relevant work-queue list. Renders nothing when
 * the work-queue visibility flag is OFF (dark).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, FileCheck2, CalendarClock } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import { t, DEFAULT_LOCALE } from '../../i18n';
import useCommercialBacklog from '../../shared/hooks/useCommercialBacklog';

const Stat = ({ icon: Icon, tone, label, value, onClick, testid }) => (
  <button
    type="button" onClick={onClick} data-testid={testid}
    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left transition hover:bg-slate-50"
  >
    <span className="flex items-center gap-2 text-[13px] font-medium text-slate-600">
      <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}><Icon className="h-4 w-4" /></span>
      {label}
    </span>
    <span className="text-[18px] font-bold tracking-tight text-slate-900">{value}</span>
  </button>
);

const OverviewCommercialBacklogCard = ({ locale = DEFAULT_LOCALE }) => {
  const navigate = useNavigate();
  const { enabled, summary } = useCommercialBacklog();
  if (!enabled) return null;

  return (
    <SectionCard title={t('overview.backlog.title', locale)} icon={ClipboardList} iconClass="bg-purple-100 text-purple-600">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="adminv3-overview-backlog">
        <Stat
          icon={FileCheck2} tone="bg-emerald-100 text-emerald-600"
          label={t('overview.backlog.acceptedDevis', locale)} value={summary.accepted_devis ?? 0}
          onClick={() => navigate('/admin/billing/devis')} testid="adminv3-backlog-devis"
        />
        <Stat
          icon={ClipboardList} tone="bg-purple-100 text-purple-600"
          label={t('overview.backlog.draftBdc', locale)} value={summary.draft_bdc ?? 0}
          onClick={() => navigate('/admin/billing/commande')} testid="adminv3-backlog-bdc"
        />
        <Stat
          icon={CalendarClock} tone="bg-indigo-100 text-indigo-600"
          label={t('overview.backlog.awaitingSchedule', locale)} value={summary.awaiting_schedule ?? 0}
          onClick={() => navigate('/admin/bookings')} testid="adminv3-backlog-awaiting"
        />
      </div>
    </SectionCard>
  );
};

export default OverviewCommercialBacklogCard;
